import React, { useState, useEffect, useRef } from 'react';
import { ref, auth, db, onAuthStateChanged, onValue, remove, set } from '../../util/firebase';
import { get, push, update } from 'firebase/database';
import {
  FaArchive,
  FaArrowDown,
  FaArrowUp,
  FaCheck,
  FaEdit,
  FaPlus,
  FaTimes,
  FaTrash,
  FaUndo
} from "react-icons/fa";
import Countdown from "../CountDown";
import TimerUp from "../TimerUP";
import Form from "./Form";
import StarterTemplates from "../Onboarding/StarterTemplates";
import "../../App.css";
import "./TodoList.css";

const TodoList = ({ onSubmit }) => {
  const FREE_TASK_LIMIT = 5;
  const [todoList, setTodoList] = useState({});
  const [calendarTasks, setCalendarTasks] = useState([]); // array of { id, title, date, ... }
  const [showTime, setShowTime] = useState(false);
  const [rando, setRando] = useState("");
  const [activeTask, setActiveTask] = useState(null);
  const [mypick, setMyPick] = useState(25);
  const [clock, setClock] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeMode, setActiveMode] = useState("");
  const [startBreak, setStartBreak] = useState(false);
  const [flowPaused, setFlowPaused] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [userPlan, setUserPlan] = useState("free");
  const [lastCompletedTask, setLastCompletedTask] = useState(null);
  const [editingTaskKey, setEditingTaskKey] = useState("");
  const [editingTitle, setEditingTitle] = useState("");
  const [taskActionError, setTaskActionError] = useState("");

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let unsubTodo = null;
    let unsubCalendar = null;
    let unsubSubscription = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        // Demo mode: provide a few calendar tasks for preview
        setDemoMode(true);
        const today = new Date();
        const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const demo = [
          { id: 'demo-1', title: 'Demo: Plan week', date: fmt(new Date(today.getTime() + 0*24*60*60*1000)) },
          { id: 'demo-2', title: 'Demo: Review goals', date: fmt(new Date(today.getTime() + 1*24*60*60*1000)) },
          { id: 'demo-3', title: 'Demo: Deep work', date: fmt(new Date(today.getTime() + 2*24*60*60*1000)) },
        ];
        setCalendarTasks(demo);
        setTodoList({});
        setUserPlan("free");
        return;
      }

      setDemoMode(false);

      const todoListRef = ref(db, `users/${user.uid}/todoList`);
      unsubTodo = onValue(todoListRef, (snapshot) => {
        if (!mountedRef.current) return;
        setTodoList(snapshot.val() || {});
      });

      const calendarRef = ref(db, `users/${user.uid}/calendar`);
      unsubCalendar = onValue(calendarRef, (snapshot) => {
        if (!mountedRef.current) return;
        const data = snapshot.val() || {};
        // Map to array for easier operations
        const arr = Object.entries(data).map(([id, ev]) => ({ id, ...ev }));
        setCalendarTasks(arr);
      });

      const subscriptionRef = ref(db, `users/${user.uid}/subscription`);
      unsubSubscription = onValue(subscriptionRef, (snapshot) => {
        if (!mountedRef.current) return;
        const subscription = snapshot.val();
        setUserPlan(subscription?.plan?.toLowerCase() || "free");
      });
    });

    return () => {
      mountedRef.current = false;
      unsubAuth();
      if (unsubTodo) unsubTodo();
      if (unsubCalendar) unsubCalendar();
      if (unsubSubscription) unsubSubscription();
    };
  }, []);

  // Keep one ordered pool so Start Mode and the visible list always agree.
  const getCombinedTasks = () => {
    const regular = Object.entries(todoList)
      .filter(([id, t]) => !t || (t.isCalendar !== true && !t.completed && !t.archived))
      .map(([id, t], index) => ({
        id,
        title: t.title,
        order: t.order,
        fallbackOrder: index,
        isCalendarTask: false,
        raw: t
      }));

    const cal = calendarTasks
      .filter(ct => !ct.completed && !ct.archived)
      .map((ct, index) => ({
        id: ct.id,
        title: ct.title,
        order: ct.order,
        fallbackOrder: regular.length + index,
        isCalendarTask: true,
        raw: ct
      }));

    return [...regular, ...cal].sort((first, second) => {
      const firstOrder = Number.isFinite(first.order) ? first.order : Number.MAX_SAFE_INTEGER;
      const secondOrder = Number.isFinite(second.order) ? second.order : Number.MAX_SAFE_INTEGER;
      return firstOrder - secondOrder || first.fallbackOrder - second.fallbackOrder;
    });
  };

  const resetModeState = () => {
    setClock(false);
    setShowTime(false);
    setStartBreak(false);
    setFlowPaused(false);
  };

  const getTodoTaskCount = () => {
    return Object.values(todoList).filter(task => (
      !task || (task.isCalendar !== true && !task.completed && !task.archived)
    )).length;
  };

  const getTaskRecord = (task) => {
    if (task.isCalendarTask) {
      return calendarTasks.find(item => item.id === task.id);
    }
    return todoList[task.id];
  };

  const setTaskStatus = async (task, status) => {
    const timestamp = Date.now();
    const record = getTaskRecord(task);

    if (demoMode) {
      const statusFields = status === "active"
        ? { completed: false, completedAt: null, archived: false, archivedAt: null }
        : status === "completed"
          ? { completed: true, completedAt: timestamp, archived: false, archivedAt: null }
          : { archived: true, archivedAt: timestamp };

      if (task.isCalendarTask) {
        setCalendarTasks(current => current.map(item => (
          item.id === task.id ? { ...item, ...statusFields } : item
        )));
      } else {
        setTodoList(current => ({
          ...current,
          [task.id]: { ...current[task.id], ...statusFields }
        }));
      }
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    const rootPath = task.isCalendarTask ? "calendar" : "todoList";
    if (status === "active") {
      await Promise.all([
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/completed`), false),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/completedAt`), null),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/archived`), false),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/archivedAt`), null),
      ]);
    } else if (status === "completed") {
      await Promise.all([
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/completed`), true),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/completedAt`), timestamp),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/archived`), false),
      ]);
    } else {
      await Promise.all([
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/archived`), true),
        set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/archivedAt`), timestamp),
      ]);
    }

    // Calendar events have a linked todo record. Keep both halves in sync.
    if (task.isCalendarTask && record?.sourceId) {
      const linkedTask = { id: record.sourceId, isCalendarTask: false };
      const linkedPath = `users/${user.uid}/todoList/${linkedTask.id}`;
      if (status === "active") {
        await Promise.all([
          set(ref(db, `${linkedPath}/completed`), false),
          set(ref(db, `${linkedPath}/completedAt`), null),
          set(ref(db, `${linkedPath}/archived`), false),
          set(ref(db, `${linkedPath}/archivedAt`), null),
        ]);
      } else if (status === "completed") {
        await Promise.all([
          set(ref(db, `${linkedPath}/completed`), true),
          set(ref(db, `${linkedPath}/completedAt`), timestamp),
          set(ref(db, `${linkedPath}/archived`), false),
        ]);
      } else {
        await Promise.all([
          set(ref(db, `${linkedPath}/archived`), true),
          set(ref(db, `${linkedPath}/archivedAt`), timestamp),
        ]);
      }
    }
  };

  const selectTask = (task) => {
    if (!task) return;
    setActiveTask(task);
    setRando(task.title);
  };

  // Generalized delete that handles both todoList and calendar tasks
  const deleteTodo = async (id, isCalendarTask = false) => {
    if (demoMode) {
      if (isCalendarTask) {
        setCalendarTasks(prev => prev.filter(t => t.id !== id));
      } else {
        const updated = { ...todoList };
        delete updated[id];
        setTodoList(updated);
      }
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) return;
      if (isCalendarTask) {
        const calRef = ref(db, `users/${user.uid}/calendar/${id}`);
        const snapshot = await get(calRef);
        const event = snapshot.val();
        if (event?.sourceId) {
          await remove(ref(db, `users/${user.uid}/todoList/${event.sourceId}`));
        }

        await remove(calRef);
        // local state will update via listener
      } else {
        await remove(ref(db, `users/${user.uid}/todoList/${id}`));
      }
    } catch (err) {
      console.error('Error deleting task', err);
    }
  };

  // Toggle persistence for a regular todo
  const toggleTodoPersistent = async (id, current) => {
    if (demoMode) {
      setTodoList(prev => {
        const copy = { ...prev };
        if (copy[id]) copy[id].persistent = !current;
        return copy;
      });
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) return;
      await set(ref(db, `users/${user.uid}/todoList/${id}/persistent`), !current);
    } catch (e) {
      console.error('Error toggling todo persistence', e);
    }
  };

  // Toggle persistence for a calendar event
  const toggleCalendarPersistent = async (id, current) => {
    if (demoMode) {
      setCalendarTasks(prev => prev.map(t => t.id === id ? { ...t, persistent: !current } : t));
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) return;
      await set(ref(db, `users/${user.uid}/calendar/${id}/persistent`), !current);
    } catch (e) {
      console.error('Error toggling calendar persistence', e);
    }
  };

  const getTaskKey = (task) => `${task.isCalendarTask ? "calendar" : "task"}-${task.id}`;

  const beginEditingTask = (task) => {
    setTaskActionError("");
    setEditingTaskKey(getTaskKey(task));
    setEditingTitle(task.title);
  };

  const cancelEditingTask = () => {
    setEditingTaskKey("");
    setEditingTitle("");
  };

  const saveTaskTitle = async (event, task) => {
    event.preventDefault();
    const title = editingTitle.trim();
    if (!title) {
      setTaskActionError("Task title cannot be empty.");
      return;
    }

    try {
      setTaskActionError("");
      if (demoMode) {
        if (task.isCalendarTask) {
          setCalendarTasks(current => current.map(item => (
            item.id === task.id ? { ...item, title } : item
          )));
        } else {
          setTodoList(current => ({
            ...current,
            [task.id]: { ...current[task.id], title }
          }));
        }
      } else {
        const user = auth.currentUser;
        if (!user) return;
        const rootPath = task.isCalendarTask ? "calendar" : "todoList";
        await set(ref(db, `users/${user.uid}/${rootPath}/${task.id}/title`), title);

        if (task.isCalendarTask && task.raw?.sourceId) {
          await set(ref(db, `users/${user.uid}/todoList/${task.raw.sourceId}/title`), title);
        }
      }

      if (activeTask && getTaskKey(activeTask) === getTaskKey(task)) {
        setActiveTask(current => ({ ...current, title }));
        setRando(title);
      }
      cancelEditingTask();
    } catch (error) {
      console.error("Error updating task", error);
      setTaskActionError("Could not update that task. Please try again.");
    }
  };

  const moveTask = async (task, direction) => {
    const tasks = getCombinedTasks();
    const currentIndex = tasks.findIndex(item => getTaskKey(item) === getTaskKey(task));
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= tasks.length) return;

    const reordered = [...tasks];
    [reordered[currentIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[currentIndex]];

    try {
      setTaskActionError("");
      if (demoMode) {
        const taskOrders = reordered.reduce((result, item, index) => ({
          ...result,
          [getTaskKey(item)]: index
        }), {});
        setTodoList(current => Object.fromEntries(
          Object.entries(current).map(([id, item]) => [
            id,
            taskOrders[`task-${id}`] === undefined
              ? item
              : { ...item, order: taskOrders[`task-${id}`] }
          ])
        ));
        setCalendarTasks(current => current.map(item => (
          taskOrders[`calendar-${item.id}`] === undefined
            ? item
            : { ...item, order: taskOrders[`calendar-${item.id}`] }
        )));
      } else {
        const user = auth.currentUser;
        if (!user) return;
        const updates = {};
        reordered.forEach((item, index) => {
          const rootPath = item.isCalendarTask ? "calendar" : "todoList";
          updates[`users/${user.uid}/${rootPath}/${item.id}/order`] = index;
        });
        await update(ref(db), updates);
      }
    } catch (error) {
      console.error("Error reordering tasks", error);
      setTaskActionError("Could not reorder your tasks. Please try again.");
    }
  };

  const randomTodo = () => {
    resetModeState();
    setActiveMode("random");

    const pool = getCombinedTasks();
    if (!pool.length) return alert('No tasks available. Please add some tasks first.');

    const selected = pool[Math.floor(Math.random() * pool.length)];
    selectTask(selected);
  };

  const startTodo = () => {
    const pool = getCombinedTasks();
    if (!pool.length) return alert('No tasks available. Please add some tasks first.');
    resetModeState();
    setActiveMode("start");
    selectTask(pool[0]);
    setMyPick(25);
    setClock(true);
  };

  const flowTodo = () => {
    const pool = getCombinedTasks();
    if (!pool.length) return alert('No tasks available. Please add some tasks first.');
    resetModeState();
    setActiveMode("flow");
    const selected = pool[currentIndex % pool.length];
    selectTask(selected);
    setCurrentIndex(prev => prev + 1);
    setShowTime(true);
  };

  const handleStartSessionComplete = () => {
    setClock(false);
    setStartBreak(true);
  };

  const completeActiveTask = async ({ startNext = false } = {}) => {
    if (!activeTask) return;

    const completedTask = activeTask;
    const nextPool = getCombinedTasks().filter(task => (
      task.id !== completedTask.id || task.isCalendarTask !== completedTask.isCalendarTask
    ));

    await setTaskStatus(activeTask, "completed");
    setLastCompletedTask(completedTask);
    setActiveTask(null);
    setRando("");
    resetModeState();

    if (startNext && nextPool.length) {
      setActiveMode("start");
      selectTask(nextPool[0]);
      setMyPick(25);
      setClock(true);
    } else {
      setActiveMode("");
    }
  };

  const continueStartMode = () => {
    setStartBreak(false);
    completeActiveTask({ startNext: true });
  };

  const undoLastCompletion = async () => {
    if (!lastCompletedTask) return;
    await setTaskStatus(lastCompletedTask, "active");
    setLastCompletedTask(null);
  };

  const archiveTask = async (task) => {
    await setTaskStatus(task, "archived");
    if (lastCompletedTask?.id === task.id && lastCompletedTask?.isCalendarTask === task.isCalendarTask) {
      setLastCompletedTask(null);
    }
  };

  const restoreTask = async (task) => {
    await setTaskStatus(task, "active");
  };

  const refreshPage = () => {
    resetModeState();
    setRando("");
    setActiveTask(null);
    setMyPick(25);
    setCurrentIndex(0);
    setActiveMode("");
    // listeners will refresh state automatically; in demo mode rehydrate
    if (demoMode) {
      // re-create demo tasks
      const today = new Date();
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      setCalendarTasks([
        { id: 'demo-1', title: 'Demo: Plan week', date: fmt(new Date(today.getTime() + 0*24*60*60*1000)) },
        { id: 'demo-2', title: 'Demo: Review goals', date: fmt(new Date(today.getTime() + 1*24*60*60*1000)) },
        { id: 'demo-3', title: 'Demo: Deep work', date: fmt(new Date(today.getTime() + 2*24*60*60*1000)) },
      ]);
    }
  };

  const addTemplateTasks = async (template) => {
    if (demoMode) {
      const generatedTasks = template.tasks.reduce((acc, title, index) => ({
        ...acc,
        [`template-${template.id}-${index}`]: {
          title,
          templateId: template.id,
          templateName: template.name,
          order: index
        }
      }), {});
      setTodoList(prev => ({ ...prev, ...generatedTasks }));
      setShowTemplates(false);
      return;
    }

    try {
      setTemplateError("");
      setTemplateSaving(true);
      const user = auth.currentUser;
      if (!user) {
        setTemplateError("Please log in to add a template.");
        return;
      }

      const availableSlots = userPlan === "free"
        ? FREE_TASK_LIMIT - getTodoTaskCount()
        : template.tasks.length;

      if (availableSlots <= 0) {
        setTemplateError(`Free plan is limited to ${FREE_TASK_LIMIT} tasks. Delete a task or upgrade to add a template.`);
        return;
      }

      const tasksToAdd = template.tasks.slice(0, availableSlots);
      const todoListRef = ref(db, `users/${user.uid}/todoList`);
      const orderBase = Date.now();
      await Promise.all(tasksToAdd.map((title, index) => {
        const newTaskRef = push(todoListRef);
        return set(newTaskRef, {
          title,
          templateId: template.id,
          templateName: template.name,
          order: orderBase + index,
          createdAt: Date.now()
        });
      }));
      setShowTemplates(false);
    } catch (err) {
      console.error("Error adding template tasks", err);
      setTemplateError("Could not add that template. Please try again.");
    } finally {
      setTemplateSaving(false);
    }
  };

  const activeTasks = getCombinedTasks();
  const completedTasks = [
    ...Object.entries(todoList)
      .filter(([, task]) => task?.isCalendar !== true && task?.completed && !task?.archived)
      .map(([id, task]) => ({ id, ...task, isCalendarTask: false })),
    ...calendarTasks
      .filter(task => task.completed && !task.archived)
      .map(task => ({ ...task, isCalendarTask: true })),
  ].sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0));
  const archivedTasks = [
    ...Object.entries(todoList)
      .filter(([, task]) => task?.isCalendar !== true && task?.archived)
      .map(([id, task]) => ({ id, ...task, isCalendarTask: false })),
    ...calendarTasks
      .filter(task => task.archived)
      .map(task => ({ ...task, isCalendarTask: true })),
  ].sort((a, b) => (b.archivedAt || 0) - (a.archivedAt || 0));

  return (
    <div className="todo-list-container">
      <div className="todo-modes">
        <button
          onClick={randomTodo}
          className="mode-button random-mode"
          title="Randomly pick one main task to focus on."
          data-tooltip="Randomly pick one main task to focus on."
          aria-label="Random mode: randomly pick one main task to focus on"
        >
          <span className="mode-icon">🎲</span><span className="mode-text">RANDOM</span>
        </button>
        <button
          onClick={startTodo}
          className="mode-button start-mode"
          title="Work through tasks in order with 25-minute focus sessions."
          data-tooltip="Work through tasks in order with 25-minute focus sessions."
          aria-label="Start mode: work through tasks in order with 25-minute focus sessions"
        >
          <span className="mode-icon">▶️</span><span className="mode-text">START</span>
        </button>
        <button
          onClick={flowTodo}
          className="mode-button flow-mode"
          title="Start an open-ended focus timer and pause for breaks anytime."
          data-tooltip="Start an open-ended focus timer and pause for breaks anytime."
          aria-label="Flow mode: start an open-ended focus timer and pause for breaks anytime"
        >
          <span className="mode-icon">🌊</span><span className="mode-text">FLOW</span>
        </button>
        <button
          onClick={refreshPage}
          className="mode-button refresh-mode"
          title="Clear the current selection and reset the mode state."
          data-tooltip="Clear the current selection and reset the mode state."
          aria-label="Refresh mode: clear the current selection and reset the mode state"
        >
          <span className="mode-icon">🔄</span><span className="mode-text">REFRESH</span>
        </button>
      </div>

      {rando && (
        <div className={`selected-task selected-task-${activeMode || "default"}`}>
          <h3>
            {activeMode === "random" && "Today's Main Task"}
            {activeMode === "start" && "Pomodoro Task"}
            {activeMode === "flow" && "Flow Task"}
            {!activeMode && "Selected Task"}
          </h3>
          <p className="selected-task-title">{rando}</p>
          {activeMode === "random" && (
            <p className="mode-helper">One main task to focus on. Press Random again to roll a different task.</p>
          )}
          {clock && (
            <Countdown
              until={mypick}
              label="25 minute focus"
              onComplete={handleStartSessionComplete}
            />
          )}
          {startBreak && (
            <div className="break-panel">
              <strong>Time for a break.</strong>
              <span>Step away, reset, then continue with the next task.</span>
              <button type="button" onClick={continueStartMode}>Mark done and start next task</button>
            </div>
          )}
          {showTime && (
            <>
              <TimerUp running={!flowPaused} />
              <button
                type="button"
                className="flow-break-button"
                onClick={() => setFlowPaused(prev => !prev)}
              >
                {flowPaused ? "Resume flow" : "Take a break"}
              </button>
            </>
          )}
          {activeTask && !startBreak && (
            <button
              type="button"
              className="complete-task-button"
              onClick={() => completeActiveTask()}
            >
              Mark task done
            </button>
          )}
        </div>
      )}

      {lastCompletedTask && (
        <div className="completion-notice" role="status">
          <span><strong>{lastCompletedTask.title}</strong> moved to Completed.</span>
          <button type="button" onClick={undoLastCompletion}>
            <FaUndo /> Undo
          </button>
        </div>
      )}

      <Form />

      <div className="todo-items">
        <div className="todo-items-header">
          <h3>Your Tasks</h3>
          <button
            type="button"
            className="template-button"
            onClick={() => setShowTemplates(true)}
          >
            <FaPlus />
            Template
          </button>
        </div>
        {taskActionError && <p className="task-action-error" role="alert">{taskActionError}</p>}
        {activeTasks.length === 0 ? (
          <p className="no-tasks">No tasks available. Add some tasks to get started!</p>
        ) : (
          <ul className="todo-list">
            {activeTasks.map((task, index) => (
              <li key={getTaskKey(task)} className={`todo-item ${task.isCalendarTask ? "calendar-task" : ""}`}>
                <span className="task-type" aria-hidden="true">{task.isCalendarTask ? "📅" : "📝"}</span>
                <div className="task-main">
                  {editingTaskKey === getTaskKey(task) ? (
                    <form className="task-edit-form" onSubmit={(event) => saveTaskTitle(event, task)}>
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(event) => setEditingTitle(event.target.value)}
                        aria-label={`Edit ${task.title}`}
                        autoFocus
                      />
                      <button type="submit" aria-label="Save task title"><FaCheck /></button>
                      <button type="button" onClick={cancelEditingTask} aria-label="Cancel editing"><FaTimes /></button>
                    </form>
                  ) : (
                    <>
                      <span className="task-title">{task.title}</span>
                      {task.isCalendarTask && <small>Calendar task</small>}
                    </>
                  )}
                </div>
                <label className="keep-task-control" title="Keep this task available for recurring use">
                  <input
                    type="checkbox"
                    checked={!!task.raw?.persistent}
                    onChange={() => task.isCalendarTask
                      ? toggleCalendarPersistent(task.id, !!task.raw?.persistent)
                      : toggleTodoPersistent(task.id, !!task.raw?.persistent)}
                  />
                  <small>Keep</small>
                </label>
                <div className="task-row-actions">
                  <button
                    type="button"
                    onClick={() => moveTask(task, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${task.title} up`}
                    title="Move up"
                  ><FaArrowUp /></button>
                  <button
                    type="button"
                    onClick={() => moveTask(task, 1)}
                    disabled={index === activeTasks.length - 1}
                    aria-label={`Move ${task.title} down`}
                    title="Move down"
                  ><FaArrowDown /></button>
                  <button
                    type="button"
                    onClick={() => beginEditingTask(task)}
                    aria-label={`Edit ${task.title}`}
                    title="Edit task"
                  ><FaEdit /></button>
                  <button
                    type="button"
                    onClick={() => deleteTodo(task.id, task.isCalendarTask)}
                    className="delete-button"
                    aria-label={`Delete ${task.title}`}
                    title="Delete task"
                  ><FaTrash /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="task-history">
        <details open={completedTasks.length > 0}>
          <summary>Completed <span>{completedTasks.length}</span></summary>
          {completedTasks.length === 0 ? (
            <p className="history-empty">Completed tasks will appear here instead of being deleted.</p>
          ) : (
            <ul className="history-list">
              {completedTasks.map(task => (
                <li key={`${task.isCalendarTask ? "calendar" : "task"}-${task.id}`}>
                  <div>
                    <span className="history-title">{task.title}</span>
                    <small>
                      {task.isCalendarTask ? "Calendar task" : "Task"}
                      {task.completedAt ? ` · ${new Date(task.completedAt).toLocaleString()}` : ""}
                    </small>
                  </div>
                  <div className="history-actions">
                    <button type="button" onClick={() => restoreTask(task)} title="Return to active tasks">
                      <FaUndo /> Restore
                    </button>
                    <button type="button" onClick={() => archiveTask(task)} title="Move to archive">
                      <FaArchive /> Archive
                    </button>
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => deleteTodo(task.id, task.isCalendarTask)}
                      title="Delete permanently"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </details>

        {archivedTasks.length > 0 && (
          <details>
            <summary>Archived <span>{archivedTasks.length}</span></summary>
            <ul className="history-list archived-list">
              {archivedTasks.map(task => (
                <li key={`${task.isCalendarTask ? "calendar" : "task"}-${task.id}`}>
                  <div>
                    <span className="history-title">{task.title}</span>
                    <small>{task.isCalendarTask ? "Calendar task" : "Task"}</small>
                  </div>
                  <div className="history-actions">
                    <button type="button" onClick={() => restoreTask(task)}>
                      <FaUndo /> Restore
                    </button>
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => deleteTodo(task.id, task.isCalendarTask)}
                    >
                      <FaTrash /> Delete permanently
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
      {showTemplates && (
        <div className="template-modal" role="dialog" aria-modal="true" aria-label="Add tasks from template">
          <div className="template-modal-content">
            <button
              type="button"
              className="template-modal-close"
              onClick={() => setShowTemplates(false)}
              aria-label="Close template picker"
              disabled={templateSaving}
            >
              ×
            </button>
            <StarterTemplates
              title="Add a task template"
              subtitle="Choose a ready-made list. Free accounts can keep up to 5 tasks total."
              onSelectTemplate={addTemplateTasks}
              onCreateCustom={() => setShowTemplates(false)}
            />
            {templateError && <p className="template-error">{templateError}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export { TodoList };
